import { useEffectOnce } from "react-use";
import { motion } from "motion/react";

import cn from "@utils/helpers/cn";
import contentWidthClassNames from "@utils/helpers/contentWidthClassNames";
import PageTitle from "@components/page/PageTitle";
import PageHeader from "@components/page/PageHeader";
import PageContent from "@components/page/PageContent";
import MotionDiv from "@components/elements/MotionDiv";
import InstructionCard from "@components/elements/InstructionCard";
import Loader from "@components/loader/Loader";

const COLUMN_CLASS_NAMES = cn(`
  flex
  flex-col
  gap-4
`);

const defaultConfig = {
  displayPageHeader: true,
  isAlignItemsStart: true,
  hasTextCentered: false,
  titleSize: "md",
  contentWidth: "md",
  instructions: null,
};

export default function Page(props) {
  const {
    className,
    noContentTitle,
    noContentMessage,
  } = props;

  const config = { ...defaultConfig, ...props.config };

  const isLoading = props.isLoading ?? false;
  const displayContent = props.displayContent ?? true;

  useEffectOnce(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  });

  const sectionClassNames = cn(`
    min-h-screen
    px-4
    pb-24
    pt-8
    md:px-8
  `, {
    "content-start": config.isAlignItemsStart,
    "content-center": !config.isAlignItemsStart,
    "text-center": config.hasTextCentered,
  });

  const columnClassNames = cn(COLUMN_CLASS_NAMES, contentWidthClassNames(config.contentWidth), className);

  /**************************************************************************************************/

  return (
    <section className={sectionClassNames}>
      <motion.div
        key="page"
        className="container mx-auto"
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
        }}
        exit={{
          opacity: 0,
        }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
          delay: 0.5,
        }}
        layout="position"
      >
        <div className={columnClassNames}>
          {config.displayPageHeader && (
            <PageHeader />
          )}

          <PageTitle
            value={config.title}
            size={config.titleSize}
          />

          {config.instructions && (
            <MotionDiv type="card">
              <InstructionCard content={config.instructions} />
            </MotionDiv>
          )}

          {isLoading ? (
            <Loader />
          ) : (
            <PageContent
              displayContent={displayContent}
              noContentTitle={noContentTitle}
              noContentMessage={noContentMessage}
            >
              {props.children}
            </PageContent>
          )}
        </div>
      </motion.div>
    </section>
  );
}
